import React, { useState, useEffect } from 'react';
import { FiX as X, FiAlertTriangle as AlertTriangle, FiAward as Crown, FiShield as Shield } from 'react-icons/fi';
import { apiCall } from '../../../../services/realApi';

interface AdminUser {
  id: string;
  name?: string;
  email: string;
  clientRole?: string;
  client_role?: string;
}

interface TransferOwnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  currentOwnerId?: string;
}

const TransferOwnershipModal: React.FC<TransferOwnershipModalProps> = ({ isOpen, onClose, onSuccess, currentOwnerId }) => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [confirmation, setConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadAdminUsers();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAdminUsers = async () => {
    setLoadingUsers(true);
    try {
      // Load all client users and filter for admin users
      const users = await apiCall('/users?user_type=client');
      const admins = (users as AdminUser[]).filter((user: AdminUser) =>
        (user.clientRole || user.client_role) === 'admin_user' && user.id !== currentOwnerId
      );
      setAdminUsers(admins);
    } catch (err) {
      console.error('Error loading admin users:', err);
      setError('Failed to load admin users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedUserId || !confirmation) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await apiCall('/users/transfer-ownership', {
        method: 'POST',
        body: JSON.stringify({
          new_owner_user_id: selectedUserId,
          confirmation: true
        })
      });

      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (err: unknown) {
      console.error('Error transferring ownership:', err);
      setError(err instanceof Error ? err.message : 'Failed to transfer ownership');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUserId('');
    setConfirmation(false);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full transition-colors">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Transfer Account Ownership
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning Banner */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-amber-900 dark:text-amber-200 mb-1">
                  Important: This action cannot be undone
                </p>
                <p className="text-amber-700 dark:text-amber-300">
                  After transferring ownership, you will become an Admin User and will no longer be able to transfer ownership again. Only the new Account Owner will have this ability.
                </p>
              </div>
            </div>
          </div>

          {/* User Selection */}
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select New Account Owner
            </span>
            {loadingUsers ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : adminUsers.length === 0 ? (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No Admin Users available. You must have at least one Admin User before transferring ownership.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                {adminUsers.map((user) => (
                  <label aria-label={`Select ${user.name || user.email} as new owner`}
                    key={user.id}
                    className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      selectedUserId === user.id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="newOwner"
                      value={user.id}
                      checked={selectedUserId === user.id}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.name || 'Unknown User'}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                          <Shield className="w-3 h-3 mr-1" />
                          Admin User
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Confirmation Checkbox */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmation}
                onChange={(e) => setConfirmation(e.target.checked)}
                disabled={!selectedUserId}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                I understand that this action is permanent and cannot be reversed. I will become an Admin User and lose Account Owner privileges.
              </span>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleTransfer}
            disabled={!selectedUserId || !confirmation || loading || adminUsers.length === 0}
            className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Transferring...</span>
              </>
            ) : (
              <>
                <Crown className="w-4 h-4" />
                <span>Transfer Ownership</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferOwnershipModal;
